import type { Target } from "~/styles/exports"
import { BREAKPOINTS } from "~/styles/exports"

const VALID_POINTER_QUERIES = new Set(["coarse", "fine"])

export type SupportedKeys =
  | Target
  | "coarse"
  | "fine"
  | "portrait"
  | "landscape"
  | (string & {})

/**
 * Helper function to get CSS media query for given shorthand `key`.
 *
 * @param key - The shorthand key to match for.
 *
 * @example
 * getMatchMediaQuery('coarse') -> Checks if the browser matches coarse; device
 * of limited accuracy (touch).
 *
 * @example
 * getMatchMediaQuery('fine') -> Checks if the browser matches fine; accurate
 * pointing device (mouse).

 * @example
 * getMatchMediaQuery('tablet') -> Checks if the browser is at or above our
 * tablet `min-width` breakpoint.
 *
 * @example
 * getMatchMediaQuery('laptop') -> Checks if the browser is at or above our
 * laptop `min-width` breakpoint.
 */
export const getMatchMediaQuery = (key: SupportedKeys) => {
  // If the key is xxs, return 'max-width' query instead. Note: We use the
  // tablet breakpoint as the 'max-width' for mobile, as we don't have a mobile
  // breakpoint + the negative 1 ensures 'max-width' fires at same point as
  // 'min-width' breakpoint
  if (key === "xxs") {
    const xsMinWidth = BREAKPOINTS.xs.minWidth
    return `(max-width: ${xsMinWidth - 1}px)`
  }

  // If the key is a valid breakpoint, return the min-width query
  if (key in BREAKPOINTS) {
    const minWidth = BREAKPOINTS[key as Target].minWidth
    if (minWidth) return `(min-width: ${minWidth}px)`
  }
  // If the key is a valid pointer query, return the pointer query
  if (VALID_POINTER_QUERIES.has(key)) return `(pointer: ${key})`

  // If the key is a valid orientation query, return the orientation query
  if (key === "portrait" || key === "landscape") return `(orientation: ${key})`

  return key
}

/**
 * Helper function to get CSS media query result for given shorthand `key`.
 *
 * @param key - The shorthand key to match for (see `getMatchMediaQuery`).
 */
export const getMatchMediaMatch = (key: SupportedKeys) => {
  const query = getMatchMediaQuery(key)
  return window.matchMedia(query).matches
}
